import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Button, Typography } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import { calculateWpm, calculateAccuracy, formatDuration } from './typingTutorUtils';
import { shuffleArray } from './typingTutorGames';
import { getFingerInstruction } from './fingerKeyMap';

/**
 * Pop letter bubbles before they fade — fingering speed drill
 */
export default function KeyPopGame({
  level,
  targetWpm = 35,
  onComplete,
  onRetry,
  onCancel,
}) {
  const keys = level?.keys || ['a', 's', 'd', 'f', 'j', 'k', 'l'];
  const duration = level?.duration || 45;

  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);
  const [remaining, setRemaining] = useState(duration);
  const [currentKey, setCurrentKey] = useState('');
  const [popAnim, setPopAnim] = useState(null);
  const [shake, setShake] = useState(false);
  const [score, setScore] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [missed, setMissed] = useState(0);
  const [wrong, setWrong] = useState(0);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const bubbleTimerRef = useRef(null);
  const statsRef = useRef({ correct: 0, wrong: 0, missed: 0, score: 0 });
  const keyQueueRef = useRef(shuffleArray(keys));

  const nextKey = useCallback(() => {
    if (keyQueueRef.current.length === 0) {
      keyQueueRef.current = shuffleArray(keys);
    }
    const k = keyQueueRef.current.shift();
    setCurrentKey(k);
    setPopAnim(null);
  }, [keys]);

  const reset = useCallback(() => {
    setStarted(false);
    setFinished(false);
    setRemaining(duration);
    setScore(0);
    setCorrect(0);
    setMissed(0);
    setWrong(0);
    setResult(null);
    setPopAnim(null);
    setShake(false);
    statsRef.current = { correct: 0, wrong: 0, missed: 0, score: 0 };
    keyQueueRef.current = shuffleArray(keys);
    if (timerRef.current) clearInterval(timerRef.current);
    if (bubbleTimerRef.current) clearTimeout(bubbleTimerRef.current);
    nextKey();
    onRetry?.();
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [duration, keys, nextKey, onRetry]);

  useEffect(() => {
    reset();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (bubbleTimerRef.current) clearTimeout(bubbleTimerRef.current);
    };
  }, [level?.id, reset]);

  const finishGame = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (bubbleTimerRef.current) clearTimeout(bubbleTimerRef.current);
    const timeSeconds = Math.max(1, duration - remaining);
    const { correct: c, wrong: w, score: s } = statsRef.current;
    const totalAttempts = c + w;
    const wpm = calculateWpm(c, timeSeconds);
    const accuracy = calculateAccuracy(c, totalAttempts);
    const sessionResult = {
      wpm,
      accuracy,
      timeSpentSeconds: timeSeconds,
      errors: w + statsRef.current.missed,
      totalChars: totalAttempts,
      correctChars: c,
      score: s,
      keysPopped: c,
      goalMet: wpm >= Math.max(20, targetWpm - 10),
    };
    setResult(sessionResult);
    setFinished(true);
    onComplete?.(sessionResult);
  }, [duration, remaining, targetWpm, onComplete]);

  useEffect(() => {
    if (!started || finished) return undefined;
    timerRef.current = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          setTimeout(finishGame, 0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [started, finished, finishGame]);

  useEffect(() => {
    if (!started || finished || !currentKey) return undefined;
    if (bubbleTimerRef.current) clearTimeout(bubbleTimerRef.current);
    bubbleTimerRef.current = setTimeout(() => {
      statsRef.current.missed += 1;
      setMissed(statsRef.current.missed);
      setShake(true);
      setTimeout(() => setShake(false), 300);
      nextKey();
    }, 2500);
    return () => {
      if (bubbleTimerRef.current) clearTimeout(bubbleTimerRef.current);
    };
  }, [started, finished, currentKey, nextKey]);

  useEffect(() => {
    const handleKey = (e) => {
      if (finished) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === 'Escape') {
        onCancel?.();
        return;
      }
      if (e.key.length !== 1) return;
      e.preventDefault();

      if (!started) setStarted(true);

      const pressed = e.key.toLowerCase();
      const expected = currentKey.toLowerCase();

      if (pressed === expected) {
        if (bubbleTimerRef.current) clearTimeout(bubbleTimerRef.current);
        statsRef.current.correct += 1;
        statsRef.current.score += 10;
        setCorrect(statsRef.current.correct);
        setScore(statsRef.current.score);
        setPopAnim('pop');
        setTimeout(() => nextKey(), 200);
      } else {
        statsRef.current.wrong += 1;
        setWrong(statsRef.current.wrong);
        setShake(true);
        setTimeout(() => setShake(false), 300);
      }
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [finished, started, currentKey, nextKey, onCancel]);

  const instruction = currentKey
    ? getFingerInstruction(currentKey, { homeRowOnly: level?.id === 'keypop-home' })
    : 'Get ready…';

  const displayKey = currentKey === ' ' ? 'Space' : currentKey.toUpperCase();

  if (finished && result) {
    return (
      <Box className="tt-result-panel tt-card">
        <Typography variant="h5" className="tt-sans" fontWeight={700} gutterBottom>
          Key Pop Complete
        </Typography>
        <Box className="tt-stats-bar" sx={{ justifyContent: 'center', mb: 2, mt: 2 }}>
          <div className="tt-stat-item">
            <div className="tt-stat-value">{result.keysPopped}</div>
            <div className="tt-stat-label">Keys Popped</div>
          </div>
          <div className="tt-stat-item">
            <div className="tt-stat-value">{result.wpm}</div>
            <div className="tt-stat-label">WPM</div>
          </div>
          <div className="tt-stat-item">
            <div className="tt-stat-value">{result.accuracy}%</div>
            <div className="tt-stat-label">Accuracy</div>
          </div>
          <div className="tt-stat-item">
            <div className="tt-stat-value">{result.score}</div>
            <div className="tt-stat-label">Score</div>
          </div>
        </Box>
        <Typography className="tt-sans" sx={{ color: result.goalMet ? 'success.main' : 'text.secondary', mb: 2 }}>
          {result.goalMet ? 'Great finger speed!' : 'Practice home row daily to improve.'}
        </Typography>
        <Button variant="outlined" startIcon={<ReplayIcon />} onClick={reset} sx={{ mr: 1 }}>
          Play Again
        </Button>
        <Button variant="contained" color="success" onClick={onCancel}>
          Back to Games
        </Button>
      </Box>
    );
  }

  return (
    <Box className={`tt-keypop-stage ${shake ? 'tt-keypop-shake' : ''}`}>
      <Box className="tt-stats-bar" sx={{ mb: 3 }}>
        <div className="tt-stat-item">
          <div className="tt-stat-value">{score}</div>
          <div className="tt-stat-label">Score</div>
        </div>
        <div className="tt-stat-item">
          <div className="tt-stat-value">{correct}</div>
          <div className="tt-stat-label">Popped</div>
        </div>
        <div className="tt-stat-item">
          <div className="tt-stat-value">{formatDuration(remaining)}</div>
          <div className="tt-stat-label">Left</div>
        </div>
        <div className="tt-stat-item">
          <div className="tt-stat-value">{missed + wrong}</div>
          <div className="tt-stat-label">Misses</div>
        </div>
      </Box>

      <Box className="tt-keypop-arena">
        <div className={`tt-keypop-bubble ${popAnim || ''}`}>
          <span className="tt-keypop-letter">{displayKey}</span>
        </div>
      </Box>

      <Typography className="tt-sans tt-keypop-hint" align="center" sx={{ mt: 2 }}>
        {instruction}
      </Typography>

      <input ref={inputRef} className="tt-hidden-input" autoFocus aria-hidden tabIndex={-1} />

      <Typography className="tt-keyboard-hint tt-sans" align="center">
        {!started ? 'Press the letter shown in the bubble. Timer starts on first key.' : 'Pop keys fast — bubble fades in 2.5 sec. Esc to exit.'}
      </Typography>
    </Box>
  );
}
